/**
 * SA-1092 §3: the Triage tab's queue read — `GET /api/inbox/queue` under the
 * store's chip filters and search, into the store's `queue` slice. A silent
 * refetch merges the head and marks arrivals (the SA-1079 shape); a plain
 * fetch replaces. A failed read keeps the items and sets the error.
 */
import { useCallback, useRef } from 'react'
import { apiClient } from '@/services/apiClient'
import { INBOX_PAGE_SIZE, useInboxStore } from '@/stores/inboxStore'
import type { QueueItem } from '@/types/inbox'

type Store = ReturnType<typeof useInboxStore.getState>

export function queueParams(s: Pick<Store, 'channelFilter' | 'kindFilter' | 'flagFilter' | 'mailboxFilter' | 'searchQuery'>, offset: number): URLSearchParams {
  const params = new URLSearchParams()
  if (s.channelFilter) params.set('channel', s.channelFilter)
  if (s.kindFilter) params.set('kind', s.kindFilter)
  if (s.flagFilter) params.set('flag', s.flagFilter)
  if (s.mailboxFilter) params.set('mailbox_id', s.mailboxFilter)
  if (s.searchQuery) params.set('search', s.searchQuery)
  params.set('limit', String(INBOX_PAGE_SIZE))
  params.set('offset', String(offset))
  return params
}

function kindsFromWire(v: unknown): QueueItem['kinds'] {
  if (!Array.isArray(v)) return []
  return v.flatMap((k) => (k && typeof k.kind === 'string'
    ? [{ kind: k.kind, kindLabel: typeof k.kind_label === 'string' ? k.kind_label : null }]
    : []))
}

function fromWire(i: Record<string, unknown>): QueueItem {
  const text = (v: unknown) => (typeof v === 'string' ? v : null)
  return {
    id: String(i.id), conversationId: String(i.conversation_id), ledgerId: text(i.ledger_id), messageId: text(i.message_id),
    channel: String(i.channel), title: text(i.title), senderName: text(i.sender_name), preview: text(i.preview),
    kind: text(i.kind), kindLabel: text(i.kind_label), kinds: kindsFromWire(i.kinds), flag: i.flag as QueueItem['flag'], decisionStatus: text(i.decision_status),
    waitingSince: String(i.waiting_since), unread: typeof i.unread === 'number' ? i.unread : 0, mailboxId: text(i.mailbox_id),
  }
}

export function useInboxQueue() {
  const queue = useInboxStore((s) => s.queue)
  const inFlight = useRef(0)

  const fetchQueue = useCallback(async (opts: { silent?: boolean } = {}) => {
    const store = useInboxStore.getState()
    const snapshot = queueParams(store, 0).toString()
    inFlight.current++
    if (!opts.silent) store.setQueueLoading(true)
    try {
      const response = await apiClient.fetch(`/api/inbox/queue?${snapshot}`)
      if (!response.ok) throw new Error(`Failed to fetch the queue: ${response.statusText}`)
      const data = await response.json()
      if (queueParams(useInboxStore.getState(), 0).toString() !== snapshot) return
      const items = (data.items as Record<string, unknown>[]).map(fromWire)
      if (opts.silent) useInboxStore.getState().mergeQueueHead(items, data.total ?? 0)
      else useInboxStore.getState().setQueue(items, data.total ?? 0)
    } catch (e) {
      useInboxStore.getState().setQueueError(e instanceof Error ? e.message : 'Failed to fetch the queue')
    } finally {
      inFlight.current--
      if (inFlight.current === 0) useInboxStore.getState().setQueueLoading(false)
    }
  }, [])

  // SA-1104: the list fetchMore's two rules — no next page while a head fetch
  // is in flight, and a page that answers after the filters changed is dropped.
  const fetchMore = useCallback(async () => {
    if (inFlight.current > 0) return
    const store = useInboxStore.getState()
    const snapshot = queueParams(store, 0).toString()
    const params = queueParams(store, store.queue.items.length)
    try {
      const response = await apiClient.fetch(`/api/inbox/queue?${params}`)
      if (!response.ok) throw new Error(`Failed to fetch the queue: ${response.statusText}`)
      const data = await response.json()
      if (queueParams(useInboxStore.getState(), 0).toString() !== snapshot) return
      useInboxStore.getState().appendQueue((data.items as Record<string, unknown>[]).map(fromWire), data.total ?? 0)
    } catch (e) {
      useInboxStore.getState().setQueueError(e instanceof Error ? e.message : 'Failed to fetch the queue')
    }
  }, [])

  const clearArrivals = useCallback(() => useInboxStore.getState().clearQueueArrivals(), [])
  return { items: queue.items, total: queue.total, isLoading: queue.isLoading, error: queue.error, arrivedIds: queue.arrivedIds, fetchQueue, fetchMore, clearArrivals }
}
